<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="本所期限：诉讼/仲裁等关键节点。可标记完成。"
      class="mb-12px"
    />
    <el-form :inline="true" :model="query" class="mb-12px" @submit.prevent>
      <el-form-item label="案件ID">
        <el-input-number v-model="query.caseId" :min="1" controls-position="right" class="!w-120px" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-120px">
          <el-option label="待处理" value="PENDING" />
          <el-option label="已完成" value="DONE" />
          <el-option label="已错过" value="MISSED" />
        </el-select>
      </el-form-item>
      <el-form-item label="即将到期">
        <el-switch v-model="query.upcomingOnly" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button type="success" @click="openCreate">新建期限</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="70" />
      <el-table-column prop="firmCaseNo" label="案号" min-width="120" />
      <el-table-column prop="deadlineType" label="类型" width="100" />
      <el-table-column prop="title" label="标题" min-width="140" show-overflow-tooltip />
      <el-table-column prop="dueDate" label="到期日" width="110" />
      <el-table-column prop="remindDaysBefore" label="提前提醒天" width="100" />
      <el-table-column prop="status" label="状态" width="90" />
      <el-table-column label="操作" width="140" fixed="right">
        <template #default="{ row }">
          <el-button
            v-if="row.status === 'PENDING' || row.status === 'MISSED'"
            link
            type="success"
            @click="doComplete(row)"
          >
            完成
          </el-button>
          <el-button link type="danger" @click="doDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="getList"
    />

    <Dialog v-model="createVisible" title="新建期限" width="520px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="案件" prop="caseId">
          <el-select v-model="form.caseId" filterable class="!w-100%">
            <el-option
              v-for="m in matterOptions"
              :key="m.id"
              :label="`${m.firmCaseNo || m.id} · ${m.title}`"
              :value="m.id!"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="类型" prop="deadlineType">
          <el-select v-model="form.deadlineType" class="!w-100%">
            <el-option label="起诉/申请" value="FILING" />
            <el-option label="答辩" value="DEFENSE" />
            <el-option label="上诉" value="APPEAL" />
            <el-option label="执行" value="ENFORCE" />
            <el-option label="其他" value="OTHER" />
          </el-select>
        </el-form-item>
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" maxlength="128" />
        </el-form-item>
        <el-form-item label="到期日" prop="dueDate">
          <el-date-picker v-model="form.dueDate" type="date" value-format="YYYY-MM-DD" class="!w-100%" />
        </el-form-item>
        <el-form-item label="提前天数">
          <el-input-number v-model="form.remindDaysBefore" :min="0" :max="90" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitCreate">保存</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import {
  FirmPracticeApi,
  type PracticeDeadlineVO,
  type PracticeMatterVO
} from '@/api/firm/practice'

defineOptions({ name: 'FirmPracticeDeadline' })

const message = useMessage()
const loading = ref(false)
const list = ref<PracticeDeadlineVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  caseId: undefined as number | undefined,
  status: undefined as string | undefined,
  upcomingOnly: false
})

const matterOptions = ref<PracticeMatterVO[]>([])
const createVisible = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({
  caseId: undefined as number | undefined,
  deadlineType: 'FILING',
  title: '',
  dueDate: '',
  remindDaysBefore: 3,
  remark: ''
})
const rules: FormRules = {
  caseId: [{ required: true, message: '请选择案件', trigger: 'change' }],
  deadlineType: [{ required: true, message: '请选择类型', trigger: 'change' }],
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  dueDate: [{ required: true, message: '请选择到期日', trigger: 'change' }]
}

const getList = async () => {
  loading.value = true
  try {
    const data = await FirmPracticeApi.deadlinePage({
      ...query,
      upcomingOnly: query.upcomingOnly || undefined
    })
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  query.pageNo = 1
  getList()
}

const openCreate = async () => {
  form.caseId = undefined
  form.deadlineType = 'FILING'
  form.title = ''
  form.dueDate = ''
  form.remindDaysBefore = 3
  form.remark = ''
  createVisible.value = true
  const matters = await FirmPracticeApi.matterPage({ pageNo: 1, pageSize: 100 })
  matterOptions.value = matters?.list || []
}

const submitCreate = async () => {
  await formRef.value?.validate()
  if (!form.caseId) return
  saving.value = true
  try {
    await FirmPracticeApi.deadlineCreate({
      caseId: form.caseId,
      deadlineType: form.deadlineType,
      title: form.title.trim(),
      dueDate: form.dueDate,
      remindDaysBefore: form.remindDaysBefore,
      remark: form.remark || undefined
    })
    message.success('已创建')
    createVisible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}

const doComplete = async (row: PracticeDeadlineVO) => {
  if (!row.id) return
  await FirmPracticeApi.deadlineComplete(row.id)
  message.success('已完成')
  await getList()
}

const doDelete = async (row: PracticeDeadlineVO) => {
  if (!row.id) return
  try {
    await message.confirm('确认删除该期限？')
  } catch {
    return
  }
  await FirmPracticeApi.deadlineDelete(row.id)
  message.success('已删除')
  await getList()
}

onMounted(getList)
</script>
